/* eslint-disable no-restricted-globals */
import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import moment from 'moment';
import { useQuery, useMutation } from '@tanstack/react-query';

import FormContainer from '../../../FormContainer';
import Table from '../../../../Table';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';
import Button from '../../../../Button';
import Distribution from './Distribution';

import { useUiContext } from '../../../../../context/UiContext';
import { useImpoundsStore, useUiStore } from '../../../../../store';
import LoanService from '../../../../../services/LoanService';
import AdvanceService from '../../../../../services/AdvanceService';
import useImpoundScheduleForm from '../../../../../forms/loan/impoundSchedule';
import useInvoices from './hooks/useInvoices';
import calculateLenderAdvance from './helpers/calculateLenderAdvance';
import calculateScheduledDistribution, {
  clearScheduledDistributionMemo,
} from './helpers/calculateScheduledDistribution';
import calculateBalance, { clearBalanceMemo } from './helpers/calculateBalance';
import calculateCushion, { clearCushionMemo } from './helpers/calculateCushion';
import useQueryParam from '../../../../../hooks/useQueryParam';
import createActualCollectedTooltipContent from './helpers/createActualCollectedTooltipContent';

export default function Impounds({ loanId, loan, refetchLoan }) {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  const { showToast } = useUiContext();

  const { id } = useParams();
  const navigate = useNavigate();

  const tabs = [
    {
      label: 'Schedule',
      activeOn: 'Impounds',
      onClick: () => navigate(`/loans/${id}?tab=Impounds`),
    },
    {
      label: 'Analysis',
    },
    {
      label: 'Initial',
    },
  ];

  const isMobile = useMediaQuery({ maxWidth: 767 });

  const sidebarWidth = useUiStore((state) => state.sidebarWidth);

  const setInvoices = useImpoundsStore((state) => state.setInvoices);
  const setBills = useImpoundsStore((state) => state.setBills);

  const { getQueryParam } = useQueryParam('tab');
  const tab = getQueryParam();

  const { data: impoundSchedule } = useQuery({
    queryKey: ['impoundSchedule', loanId],
    queryFn: () =>
      LoanService.getLoanPaymentSchedule({ loanId, type: 'impound' }),
  });

  const { data: advances } = useQuery({
    queryKey: ['advances', loanId],
    queryFn: () => AdvanceService.getAdvances(loanId),
    enabled: !!loanId,
  });

  const sortedInvoices = useInvoices(
    impoundSchedule || [],
    impoundSchedule?._bills || []
  );

  // Calculating invoice balance, cushion and scheduled distribution is heavy computation, so we use memoization to avoid recalculating the same values
  // When component unmounts, we clear the memoization cache
  useEffect(() => {
    return () => {
      clearBalanceMemo();
      clearCushionMemo();
      clearScheduledDistributionMemo();
    };
  }, []);

  const cols = useMemo(
    () => [
      {
        keys: ['sequence'],
        title: 'Id',
        fullRow: (row) => row.sequence === 'PROJECTED',
        spanColClassName:
          'bg-[#FDFC67] flex justify-center whitespace-nowrap px-2 text-[12px]',
      },
      {
        keys: ['date'],
        title: 'Period',
        format: (value) =>
          value === null ? '' : moment(value).format('M/D/YY'),
      },
      {
        keys: ['it_was_impound_balance_previously'],
        title: 'Balance',
        type: 'optionalTextInput',
        inputType: 'currency',
        isVisible: () => true,
        format: (value, row) =>
          calculateBalance({
            value,
            row,
            sortedInvoices,
            bills: impoundSchedule?._bills,
          }),
        readOnly: true,
      },
      {
        keys: ['it_was_impound_scheduled_previously'],
        title: 'Scheduled Collection',
        type: 'optionalTextInput',
        inputType: 'currency',
        isVisible: () => true,
        format: (value, row) => {
          const currentValue = row._impounds_total?.invoice_total || 0;

          return +currentValue === 0 || row.status === 'paid'
            ? '-'
            : currentValue;
        },
        readOnly: true,
      },
      {
        // impound_collected was replaced with _paid_impounds.amount_paid.
        keys: ['_paid_impounds.amount_paid'],
        title: 'Actual Collected',
        type: 'optionalTextInput',
        inputType: 'currency',
        isVisible: () => true,
        format: (value, row) => {
          if (row.status !== 'paid') {
            return '-';
          }

          return +value === 0 ? '-' : value;
        },
        tooltip: (row) =>
          row.status === 'paid'
            ? createActualCollectedTooltipContent(row._payments, loanId)
            : null,
        readOnly: true,
        fixedTooltip: true,
      },
      {
        keys: ['it_was_impound_scheduled_before'],
        title: 'Distribution',
        type: 'component',
        component: Distribution,
        props: (row) => {
          const { sum, amounts } = calculateScheduledDistribution({
            row,
            bills: impoundSchedule?._bills,
          });

          return { sum, amounts };
        },
      },
      {
        keys: ['description'],
        title: 'Description',
      },
      {
        keys: ['doesntexist2'],
        title: 'Lender Advance',
        format: (value, row) => calculateLenderAdvance(row, advances),
        type: 'optionalTextInput',
        inputType: 'currency',
        isVisible: () => true,
        readOnly: true,
      },
      {
        keys: ['notpresent'],
        title: 'Cushion',
        inputType: 'currency',
        format: (_, row) =>
          calculateCushion({
            row,
            sortedInvoices,
            bills: impoundSchedule?._bills,
          }),
      },
    ],
    [sortedInvoices, impoundSchedule, advances, loanId]
  );

  const { mutate: updateLoan } = useMutation({
    mutationFn: (formValues) => {
      return LoanService.updateLoan({
        formValues,
        loanId: loan.id,
      });
    },
    onSuccess: () => {
      showToast({
        text: 'Loan updated',
        type: 'success',
      });
      refetchLoan();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const form = useImpoundScheduleForm(updateLoan, loan);

  const rowStyle = useCallback(
    (row) => {
      let style = 'align-top';

      if (row.status === 'pending') {
        style += ' italic';
      }

      if (row.id === selectedInvoiceId) {
        style += ' font-bold';
      }

      if (row.isProjected) {
        style += ' bg-[#FDFC67] cursor-pointer';
      }

      return style;
    },
    [selectedInvoiceId]
  );

  const onRowClick = useCallback((row) => {
    if (row.isProjected) {
      setSelectedInvoiceId(row.id);
    }
  }, []);

  return (
    <div>
      <div className="mb-4 flex justify-between md:items-center flex-col md:flex-row gap-2 h-[37px]">
        <AddOrEditPageTabs tabs={tabs} />
        {selectedInvoiceId && (
          <Button
            className="!w-auto"
            onClick={() => {
              setInvoices(sortedInvoices);
              setBills(impoundSchedule?._bills);
              navigate(
                `/loans/${id}?tab=Start Analysis&invoiceId=${selectedInvoiceId}`
              );
            }}
          >
            Start Analysis
          </Button>
        )}
      </div>
      <div
        style={{
          width: isMobile ? '' : `calc(100vw - 234px - ${sidebarWidth}px)`,
        }}
        className="max-w-[1296px] p-7 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 overflow-x-auto"
      >
        <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold flex justify-between w-full">
          Impound Schedule
        </h2>
        {tab === 'Initial' && (
          <FormContainer form={form} isVisible noBillComId noTitle />
        )}
        {tab === 'Impounds' && (
          <div className="mt-6">
            <Table
              compact
              noCount
              title="Invoices"
              cols={cols}
              rows={sortedInvoices}
              rowStyle={rowStyle}
              onRowClick={onRowClick}
            />
          </div>
        )}
      </div>
    </div>
  );
}
